import { getIresDeviceCategoryFromSerialNumber, IresDevice, IresDeviceCategory } from "@/data/models/IresDevice";
import { BranchFilter } from "@/data/models/BranchFilter";
import {
  HyDryMessagesSettings,
  calculateVisibleHyDryEventCategories,
} from "@/data/models/HyDryMessagesConfig";
import User from "@/data/models/User";
import {
  getHyDryPanelEventNumber,
  HyDryEventCategory,
  HyDryPanelEvent,
  isHyDryEventLogAllowedByCapabilities,
  LatestHyDryEventMessageInfo,
  resolveEffectiveHyDryEventCategory,
} from "@/data/models/HyDryEvent";
import {
  seeAllEventLogsAccess,
  seeLidAndPauseEventLogsAccess,
} from "@/permissions/UserPermissions";

export enum HyDryMessageFilterType {
  MY_MESSAGES = "1",
  //MESSAGES_FOR_USER_ROLE = "2",
  MESSAGES_FOR_USER_CAPABILITY = "2",
  ALL_MESSAGES = "3",
  MAILBOX = "4",
}

export class HyDryEventMessagesFilter {
  targetUser: User | null;
  branchFilter: BranchFilter;

  private visibleDevices: Map<HyDryMessageFilterType, Set<string>>;
  private visibleHyDryEventCategories: Map<
    HyDryMessageFilterType,
    Set<HyDryEventCategory>
  >;

  static createFilterFromSettings(
    user: User | null,
    branchFilter: BranchFilter,
    iresDevices: IresDevice[],
    hyDryMessageSettings: HyDryMessagesSettings
  ) {
    const visibleHyDryEventCategories = new Map<
      HyDryMessageFilterType,
      Set<HyDryEventCategory>
    >();

    const types = [
      HyDryMessageFilterType.MY_MESSAGES,
      HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY,
      HyDryMessageFilterType.ALL_MESSAGES,
    ];

    types.forEach((value) => {
      visibleHyDryEventCategories.set(
        value,
        calculateVisibleHyDryEventCategories(value, user, hyDryMessageSettings)
      );
    });

    const {
      showMessagesToProjectOwners,
      showMessagesToProjectSupervisors,
    } = hyDryMessageSettings.messageFilters;

    const userId = user?.id ?? "";

    const devicesAssignedToMyProjects = new Set<string>();
    const devicesBelongingToNoUsers = new Set<string>();
    const allDevices = new Set<string>();

    for (let i = 0; i < iresDevices.length; i++) {
      const device = iresDevices[i];

      if (!branchFilter.doesMatchIresDevice(device)) {
        continue;
      }

      const serialNumber = device.serialNo;
      allDevices.add(serialNumber);

      const currentAssignment = device.currentAssignment;

      if (currentAssignment && currentAssignment.currentProjectId !== "") {
        if (
          currentAssignment.currentProjectResponsibleUserIds &&
          currentAssignment.currentProjectResponsibleUserIds.length > 0
        ) {
          if (
            currentAssignment.currentProjectResponsibleUserIds.includes(userId)
          ) {
            const isOwner = userId === currentAssignment.currentProjectOwnerId;
            const isSupervisor =
              userId === currentAssignment.currentProjectSupervisorId;

            if (isOwner || isSupervisor) {
              if (isOwner && showMessagesToProjectOwners) {
                devicesAssignedToMyProjects.add(serialNumber);
              }

              if (isSupervisor && showMessagesToProjectSupervisors) {
                devicesAssignedToMyProjects.add(serialNumber);
              }
            } else if (
              showMessagesToProjectOwners ||
              showMessagesToProjectSupervisors
            ) {
              devicesAssignedToMyProjects.add(serialNumber);
            }
          }
        } else {
          devicesBelongingToNoUsers.add(serialNumber);
        }
      } else {
        devicesBelongingToNoUsers.add(serialNumber);
      }
    }

    const visibleDevices = new Map<HyDryMessageFilterType, Set<string>>();

    visibleDevices.set(HyDryMessageFilterType.ALL_MESSAGES, allDevices);
    visibleDevices.set(
      HyDryMessageFilterType.MY_MESSAGES,
      devicesAssignedToMyProjects
    );
    visibleDevices.set(
      HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY,
      devicesBelongingToNoUsers
    );

    return new HyDryEventMessagesFilter(
      user,
      branchFilter,
      visibleHyDryEventCategories,
      visibleDevices
    );
  }

  private constructor(
    user: User | null,
    branchFilter: BranchFilter,
    visibleHyDryEventCategories: Map<
      HyDryMessageFilterType,
      Set<HyDryEventCategory>
    >,
    visibleDevices: Map<HyDryMessageFilterType, Set<string>>
  ) {
    this.visibleHyDryEventCategories = visibleHyDryEventCategories;

    this.targetUser = user;
    this.branchFilter = branchFilter;
    this.visibleDevices = visibleDevices;
  }

  getFilteredMessageForFilterType(
    messages: LatestHyDryEventMessageInfo[],
    filterType: HyDryMessageFilterType
  ): LatestHyDryEventMessageInfo[] {
    switch (filterType) {
      case HyDryMessageFilterType.MAILBOX:
        return [];
      case HyDryMessageFilterType.ALL_MESSAGES:
        return this.filterAllMessages(messages);
      case HyDryMessageFilterType.MY_MESSAGES:
        return this.filterMyMessages(messages);
      case HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY:
        return this.filterMessagesForCapability(messages);
    }
  }

  filterMessagesForCapability(messages: LatestHyDryEventMessageInfo[]) {
    return this.filterMessages(
      messages,
      HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY
    );
  }

  filterAllMessages(messages: LatestHyDryEventMessageInfo[]) {
    return this.filterMessages(messages, HyDryMessageFilterType.ALL_MESSAGES);
  }

  filterMyMessages(messages: LatestHyDryEventMessageInfo[]) {
    return this.filterMessages(messages, HyDryMessageFilterType.MY_MESSAGES);
  }

  filterMessages(
    messages: LatestHyDryEventMessageInfo[],
    filterType: HyDryMessageFilterType
  ) {
    const categorySet = this.getVisibleHyDryEventCategories(filterType);
    const devices = this.getVisibleDevices(filterType);

    // The same access gate the Logbuch uses, so a client never gets a message for an event they
    // cannot find in the log. Both capabilities are IRES-only.
    const access = {
      canSeeAllEventLogs: seeAllEventLogsAccess(this.targetUser),
      canSeeLidAndPauseEventLogs: seeLidAndPauseEventLogsAccess(this.targetUser),
    };

    const filteredMessages: LatestHyDryEventMessageInfo[] = [];
    const logsStartingFrom = new Date("2023/08/05");

    messages.forEach((message) => {
      if (!devices.has(message.serialNumber)) {
        return;
      }

      const isPanel = getIresDeviceCategoryFromSerialNumber(message.serialNumber) === IresDeviceCategory.PANEL;

      let matchingMessages = message.unreadMessages.filter((message) =>
        categorySet.has(message.eventCategory)
      );

      matchingMessages = matchingMessages.filter((unreadMessage) => {
        const latestMessage = unreadMessage.latestMessage;

        return isHyDryEventLogAllowedByCapabilities(
          {
            eventNumber: latestMessage.eventNumber,
            eventCategory: resolveEffectiveHyDryEventCategory(
              latestMessage.eventNumber,
              latestMessage.eventCategory,
              latestMessage.additionalData,
              isPanel
            ),
            isPanel: isPanel,
          },
          access
        );
      });

      if (isPanel) {
        matchingMessages = matchingMessages.filter((message) => {
          return message.latestMessage.messageDate >= logsStartingFrom && message.latestMessage.eventNumber !== getHyDryPanelEventNumber(HyDryPanelEvent.STEP);
        });
      }

      if (matchingMessages.length === 0) {
        return;
      }

      filteredMessages.push({
        serialNumber: message.serialNumber,
        unreadMessages: matchingMessages,
      });
    });

    return filteredMessages.sort(
      (a, b) =>
        b.unreadMessages[0].latestMessage.messageDate.getTime() -
        a.unreadMessages[0].latestMessage.messageDate.getTime()
    );
  }

  getVisibleDevices(filterType: HyDryMessageFilterType) {
    return this.visibleDevices.get(filterType) ?? new Set();
  }

  getVisibleHyDryEventCategories(filterType: HyDryMessageFilterType) {
    return this.visibleHyDryEventCategories.get(filterType) ?? new Set();
  }

  updateVisibleHyDryEventCategories(
    filterType: HyDryMessageFilterType,
    visibleCategores: HyDryEventCategory[]
  ) {
    this.visibleHyDryEventCategories.set(filterType, new Set(visibleCategores));
  }
}
